import { useQuery } from '@tanstack/react-query'
import { searchExact } from '../lib/search-service'
import { useOffline } from '@/components/offline-context'
import type { SearchResult } from '../types/dictionary'

export function useAlternatePronunciations(
  char: string,
  currentPinyin: string,
  scriptType: 'simplified' | 'traditional'
) {
  const { isOfflineEnabled, isReady, isQueryEnabled } = useOffline()
  const isOffline = isOfflineEnabled && isReady

  return useQuery({
    // Remove isOffline from query key to prevent cache invalidation
    // The queryFn will handle offline/online mode internally
    queryKey: ['alternatePronunciations', char, currentPinyin, scriptType],
    queryFn: async () => {
      if (!char || char.length !== 1) return []

      const results = await searchExact(char, scriptType, isOffline)

      // Filter out entries with the same pinyin as current
      return results.filter((r) => {
        const rPinyin = r.pinyin || ''
        return rPinyin !== currentPinyin && rPinyin.length > 0
      }) as SearchResult[]
    },
    enabled: isQueryEnabled(char.length === 1 && currentPinyin.length > 0),
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
    placeholderData: (previousData) => previousData, // Keep previous data visible during transitions
    gcTime: 1000 * 60 * 60 * 2, // Keep in cache for 2 hours
    refetchOnMount: false, // Don't refetch when component mounts if data is fresh
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnReconnect: false // Don't refetch on reconnect
  })
}
